import React, { useEffect, useState } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { PackingListService } from '../../services/packingList.service';
import { PERMISSIONS } from '../../constants/permissions';
import { PackingListDTO } from '../../interfaces/commercial.interface';
import { triggerDownload } from '../statutoryRegisters/shared';
import PackingListCartonPanel from './PackingListCartonPanel';

/** Packing list header detail (15.2) — mirrors ExportLcDetail's load/tabs-less single-panel
 * shape, plus a "Download PDF" button (blob download, mirrors BondRegisterReport's XLSX idiom). */
const PackingListDetail: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [pl, setPl] = useState<PackingListDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setPl(await PackingListService.getById(institutionId, id));
        } catch (e) {
            toast.showApiError(e, 'Failed to load packing list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = async () => {
        if (!pl) return;
        setDownloading(true);
        try {
            const blob = await PackingListService.downloadPackingListPdf(institutionId, pl.id);
            triggerDownload(blob, `packing-list-${pl.packingListNumber}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!pl) return <Box sx={{ p: 3 }}><Typography>Packing list not found.</Typography></Box>;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.plDetail.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">{pl.packingListNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">{pl.destination ?? '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button data-help="commercial.plDetail.download" startIcon={<FileDownloadIcon />} variant="outlined" disabled={downloading} onClick={handleDownload}>
                        {downloading ? 'Downloading…' : 'Download PDF'}
                    </Button>
                    {canManage && (
                        <Button data-help="commercial.plDetail.edit" startIcon={<EditIcon />} variant="outlined" onClick={() => navigate(`/commercial/packing-lists/${pl.id}/edit`)}>
                            Edit
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Packing Date</Typography>
                        <Typography>{pl.packingDate ? formatDate(pl.packingDate) : '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Total Cartons</Typography>
                        <Typography>{pl.totalCartons}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Total Quantity</Typography>
                        <Typography>{pl.totalQuantity}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Net / Gross Weight (kg)</Typography>
                        <Typography>{pl.totalNetWeight} / {pl.totalGrossWeight}</Typography>
                    </Grid>
                    {pl.remarks && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Remarks</Typography>
                            <Typography>{pl.remarks}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <PackingListCartonPanel packingListId={pl.id} onCartonsChanged={load} />
        </Box>
    );
};

export default PackingListDetail;
